import { create } from 'zustand';
import api, { buildQuery } from '../lib/api';

// PBX integration store — drives PbxManagementSection (embedded inside
// /config/webrtc since v9.47) AND the UserManagement SIP modal's
// "Esistente / Crea nuova" sections. All endpoints are SUPER_ADMIN-gated
// server-side; this store doesn't re-check.

const unwrap = (resp) => (resp && resp.data !== undefined ? resp.data : resp);

export const usePbxStore = create((set, get) => ({
  // Interni e inventario del centralino: dipendono da chi li chiede.
  azzera: () => set({ servers: [], inventory: {} }),

  servers: [],
  loadingServers: false,
  // Per-PBX inventory cache: { [pbxId]: { items, total, counts, fetchedAt } }
  inventory: {},
  loadingInventory: false,

  loadServers: async () => {
    set({ loadingServers: true });
    try {
      const data = unwrap(await api.get('/pbx'));
      // Defensive: if the proxy returns the SPA fallback (HTML), the upstream
      // is missing, etc., `data` may not be an array. Never store anything
      // other than an array — protects the render path (PbxManagementSection +
      // UserManagement SIP modal) from `.map`/`.length` crashes.
      const safe = Array.isArray(data) ? data : [];
      set({ servers: safe });
      return safe;
    } catch (e) {
      set({ servers: [] });
      throw e;
    } finally {
      set({ loadingServers: false });
    }
  },
  createServer: async (payload) => {
    const data = unwrap(await api.post('/pbx', payload));
    await get().loadServers();
    return data;
  },
  updateServer: async (id, payload) => {
    const data = unwrap(await api.patch(`/pbx/${id}`, payload));
    await get().loadServers();
    return data;
  },
  deleteServer: async (id) => {
    await api.delete(`/pbx/${id}`);
    await get().loadServers();
  },
  setDefault: async (id) => {
    await api.post(`/pbx/${id}/set-default`, {});
    await get().loadServers();
  },
  testConnection: async (id) => unwrap(await api.post(`/pbx/${id}/test-connection`, {})),
  checkAcl: async (id) => unwrap(await api.post(`/pbx/${id}/check-acl`, {})),
  syncNow: async (id) => unwrap(await api.post(`/pbx/${id}/sync`, {})),

  loadInventory: async (pbxId, { status, q, page, limit } = {}) => {
    set({ loadingInventory: true });
    try {
      const data = unwrap(await api.get(`/pbx/${pbxId}/extensions${buildQuery({ status, q, page, limit })}`));
      const safe = (data && typeof data === 'object' && Array.isArray(data.items))
        ? data
        : { items: [], total: 0, page: 1, limit: limit || 50, counts: { inuse: 0, free: 0, orphan: 0 } };
      set((s) => ({ inventory: { ...s.inventory, [pbxId]: { ...safe, fetchedAt: Date.now() } } }));
      return safe;
    } finally {
      set({ loadingInventory: false });
    }
  },
  // Sibling inventories — Users + Terminals (devices). Same defensive shape
  // guard as loadInventory so a misroute can never crash the render path.
  loadUsersInventory: async (pbxId, { q, page, limit } = {}) => {
    set({ loadingInventory: true });
    try {
      const data = unwrap(await api.get(`/pbx/${pbxId}/users${buildQuery({ q, page, limit })}`));
      return (data && typeof data === 'object' && Array.isArray(data.items))
        ? data
        : { items: [], total: 0, page: 1, limit: limit || 50, counts: { bound: 0, orphan: 0 } };
    } finally {
      set({ loadingInventory: false });
    }
  },
  loadTerminalsInventory: async (pbxId, { page, limit } = {}) => {
    set({ loadingInventory: true });
    try {
      const data = unwrap(await api.get(`/pbx/${pbxId}/terminals${buildQuery({ page, limit })}`));
      return (data && typeof data === 'object' && Array.isArray(data.items))
        ? data
        : { items: [], total: 0, page: 1, limit: limit || 50, counts: { bound: 0, orphan: 0 } };
    } finally {
      set({ loadingInventory: false });
    }
  },

  // Per-user binding actions — called from UserManagement SIP modal.
  bindExtension: async (userId, { pbxId, extensionNumber, password }) =>
    unwrap(await api.post(`/pbx/users/${userId}/sip-extension`, { pbxId, mode: 'bind', extensionNumber, password })),
  createExtensionForUser: async (userId, payload) =>
    unwrap(await api.post(`/pbx/users/${userId}/sip-extension`, { ...payload, mode: 'create' })),
  unbindExtension: async (userId, { deleteOnPbx = false } = {}) =>
    unwrap(await api.delete(`/pbx/users/${userId}/sip-extension${buildQuery({ deleteOnPbx })}`)),
}));
